import {Location, LocationRepository, ControlMode, ZoomType} from "location.core";
import {EventEmitter} from "main.core.events";

/**
 * Class for the autocomplete locations and addresses inputs
 */
export default class Map extends EventEmitter
{
	static #onChangedEvent = 'onChanged';

	/** {string} */
	#languageId;
	/** {google.maps.Map} */
	#googleMap;
	/** {Promise}*/
	#loaderPromise;
	/** {GoogleSource} */
	#googleSource;
	/** {number} */
	#zoom;
	/** {google.maps.Marker} */
	#locationMarker;
	/** {ControlMode}*/
	#mode;

	#geocoder;
	#locationRepository;
	#defaultPosition;
	#changeDelay;
	#timerId = '';
	#isUpdating = false;

	constructor(props)
	{
		super(props);
		this.#languageId = props.languageId;
		this.#googleSource = props.googleSource;
		this.#zoom = props.zoom || 10;
		let location = props.location || null;
		this.#locationRepository = props.locationRepository || new LocationRepository();
		this.#defaultPosition = props.defaultPosition || {latitude: 54.719208, longitude: 20.488515};
		this.#changeDelay = props.changeDelay || 500;
		this.#mode = props.mode;

		//Because googleSource could still be in the process of loading
		this.#loaderPromise = props.loaderPromise
			.then(() => {
				this.#initGoogleMap(props.mapContainer, location, this.#zoom);
			});
	}

	set mode(mode: string)
	{
		this.#mode = mode;
		this.#locationMarker.setDraggable(mode === ControlMode.edit);
	}

	#convertLocationToPosition(location: ?Location): Object
	{
		let lat, lon;

		if(location)
		{
			lat = location.latitude;
			lon = location.longitude;
		}
		else
		{
			lat = this.#defaultPosition.latitude;
			lon = this.#defaultPosition.longitude;
		}

		return new google.maps.LatLng(lat, lon);
	}

	#getPositionToLocationPromise(position): Promise
	{
		return new Promise( (resolve) => {
			this.#geocoder.geocode({'location': position}, (results, status)  => {
				if (status === 'OK' && results[0])
				{
					resolve(results[0].place_id);
				}
				else if(status === 'ZERO_RESULTS')
				{
					resolve('');
				}
				else
				{
					throw Error('Geocoder failed due to: ' + status);
				}
			});
		})
		.then((placeId) => {
			let result;

			if(placeId)
			{
				result = this.#locationRepository.findByExternalId(
					placeId,
					this.#googleSource.sourceCode,
					this.#languageId
				);
			}
			else
			{
				result = new Promise((resolve) => {
					resolve(null);
				});
			}

			return result;
		});
	}

	update(location: Location)
	{
		let position = this.#convertLocationToPosition(location);
		this.#isUpdating = true;
		this.#locationMarker.setPosition(position);
		this.#isUpdating = false;
		this.#googleMap.panTo(position);
	}

	onChangedEventSubscribe(listener: Function): void
	{
		this.subscribe(Map.#onChangedEvent, listener);
	}

	#emitOnChangedEvent(location: ?Location)
	{
		if(this.#mode === ControlMode.edit)
		{
			this.emit(Map.#onChangedEvent, { location: location	});
		}
	}

	#onMarkerUpdatePosition()
	{
		if(!this.#isUpdating && this.#mode === ControlMode.edit)
		{
			this.#createTimer(this.#locationMarker.getPosition());
		}
	}

	#createTimer(position)
	{
		if(this.#timerId !== null)
		{
			clearTimeout(this.#timerId);
		}

		this.#timerId = setTimeout(() => {
				this.#timerId = null;
				this.#googleMap.panTo(position);
				this.#fulfillOnChangedEvent(position);
			},
			this.#changeDelay
		);
	}

	#fulfillOnChangedEvent(position)
	{
		this.#getPositionToLocationPromise(position)
		.then(this.#emitOnChangedEvent.bind(this));
	}

	#onMapClick(position)
	{
		if(this.#mode === ControlMode.edit)
		{
			this.#locationMarker.setPosition(position);
			this.#createTimer(position);
		}
	}

	#initGoogleMap(mapNode: Element, location: ?Location, zoom: number): void
	{
		if(typeof google === 'undefined' || typeof google.maps.Map === 'undefined')
		{
			throw new Error('google.maps.Map must be defined');
		}

		let position = this.#convertLocationToPosition(location);

		this.#googleMap = new google.maps.Map(mapNode, {
			center: position,
			zoom: zoom
		});

		this.#googleMap.addListener('click', (e) => {
			this.#onMapClick(e.latLng);
		});

		this.#googleMap.addListener('zoom_changed', (e) => {
			console.log('zoom_changed');
			console.log(this.#googleMap.getZoom());
		});

		if(typeof google.maps.Marker === 'undefined')
		{
			throw new Error('google.maps.Marker must be defined');
		}

		this.#locationMarker = new google.maps.Marker({
			position: position,
			map: this.#googleMap,
			animation: google.maps.Animation.DROP,
			draggable: this.#mode === ControlMode.edit,
		});

		this.#locationMarker.addListener('position_changed', () => {
			this.#onMarkerUpdatePosition();
		});

		if(typeof google.maps.Geocoder === 'undefined')
		{
			throw new Error('google.maps.Geocoder must be defined');
		}

		this.#geocoder = new google.maps.Geocoder;
	}

	#convertInnerZoomToGoogle(innerZoom: number)
	{
		let result;

		if(innerZoom <= ZoomType.World)
			result = 1;
		else if(innerZoom <= ZoomType.Country)
			result = 4;
		else if(innerZoom <= ZoomType.Region)
			result = 6;
		else if(innerZoom <= ZoomType.City)
			result = 11;
		else if(innerZoom <= ZoomType.Street)
			result = 16;
		else
			result = 18;

		return result;
	}

	#convertGoogleZoomToInner(gZoom: number)
	{
		let result;

		if(gZoom <= 5)
			result = ZoomType.World;
		else if(gZoom <= 7)
			result = ZoomType.Country;
		else if(gZoom <= 9)
			result = ZoomType.Region;
		else if(gZoom <= 10)
			result = ZoomType.City;
		else if(gZoom <= 15)
			result = ZoomType.Street;
		else if(gZoom > 15)
			result = ZoomType.Building;

		return result;
	}

	set zoom(innerZoom: number): void
	{
		if(this.#googleMap)
		{
			this.#googleMap.setZoom(
				this.#convertInnerZoomToGoogle(innerZoom)
			);
		}
	}

	get zoom(): number
	{
		if(this.#googleMap)
		{
			return this.#convertGoogleZoomToInner(
				this.#googleMap.getZoom()
			);
		}
	}
}